import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useB2C } from "@/context/B2CAuthContext";
import { IMAGES } from "@/lib/images";
import { StarsDisplay } from "@/components/Stars";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { COUNTRIES, countryFlag, countryName } from "@/lib/countries";
import { useLang } from "@/context/LanguageContext";
import { usePageTitle } from "@/hooks/usePageTitle";
import { CountUp } from "@/components/CountUp";
import { formatRemaining, useTicker } from "@/lib/boost";
import { BoostedCarousel } from "@/components/BoostedCarousel";
import { BoostPromoBanner } from "@/components/BoostPromoBanner";
import Pagination, { paginate } from "@/components/Pagination";
import { Search, BadgeCheck, MapPin, ArrowRight, Sparkles } from "lucide-react";

const COVERS = [IMAGES.reception, IMAGES.conciergeDesk, IMAGES.hallway, IMAGES.heroLobby, IMAGES.receptionist, IMAGES.poolSunset];
const IMG = (u) => (u ? `${process.env.REACT_APP_BACKEND_URL}${u}` : null);
const coverFor = (id) => {
  let h = 0;
  for (let i = 0; i < (id || "").length; i++) h = (h * 31 + id.charCodeAt(i)) % 997;
  return COVERS[h % COVERS.length];
};

function PartnerCard({ p, t, index = 0 }) {
  const isCertified = p.ratings?.certified;
  return (
    <Link to={p.establishment_id ? `/partenaires/${p.id}?e=${p.establishment_id}` : `/partenaires/${p.id}`} data-testid={`partner-card-${p.establishment_id || p.id}`}
      style={{ animationDelay: `${Math.min(index, 11) * 55}ms` }}
      className={`card-enter block group bg-white rounded-[1.5rem] overflow-hidden card-shadow transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl ${p.boosted ? "ring-2 ring-findo-red" : ""}`}>
      <div className="relative h-28 overflow-hidden photo-scrim">
        <img src={IMG(p.cover_url) || coverFor(p.id)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" draggable="false" />
        <span className={`absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${isCertified ? "badge-red" : "bg-white/90 text-findo-navy"}`}>
          {isCertified ? <><BadgeCheck size={13} /> {t("par.certifiedBadge")}</> : t("par.partnerBadge")}
        </span>
        {p.boosted && (
          <span className="absolute top-3 right-3 inline-flex items-center gap-1 boost-shine rounded-full bg-findo-red text-white px-2.5 py-1 text-xs font-bold shadow" data-testid="partner-boost-badge">
            <Sparkles size={12} /> {t("an.boosted")}
          </span>
        )}
      </div>
      <div className="px-4 pt-4 pb-4">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 shrink-0 rounded-2xl overflow-hidden bg-white border border-slate-200 shadow-sm grid place-items-center p-1" data-testid={`partner-logo-${p.establishment_id || p.id}`}>
            {p.logo_url ? <img src={IMG(p.logo_url)} alt="" loading="lazy" decoding="async" className="max-w-full max-h-full object-contain" draggable="false" /> : <span className="text-2xl">{countryFlag(p.country)}</span>}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-display text-lg font-semibold text-findo-navy truncate">{p.org_name}</h3>
            <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5"><MapPin size={12} /> {countryName(p.country)}</p>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <StarsDisplay value={p.ratings?.average ?? 0} size={14} />
            <span className="text-xs text-slate-400">{p.ratings?.count ?? 0} {t("c.reviews")}</span>
          </div>
          <span className="text-findo-red text-sm font-bold flex items-center gap-1 group-hover:gap-2 transition-all">{t("par.declare")} <ArrowRight size={14} /></span>
        </div>
      </div>
    </Link>
  );
}

export default function Partners() {
  const { t } = useLang();
  usePageTitle(t("par.title"));
  const { user } = useB2C();
  const [list, setList] = useState(null);
  useTicker();
  const [q, setQ] = useState("");
  const [country, setCountry] = useState(null); // resolved default: account → IP → FR
  const [city, setCity] = useState("");
  const [cities, setCities] = useState([]);
  const [certified, setCertified] = useState(false);
  const [page, setPage] = useState(1);

  // Load the list of available cities whenever the country changes (dropdown).
  useEffect(() => {
    if (!country) return;
    setCity("");
    api(`/partners/public/cities?country=${encodeURIComponent(country)}`, { auth: false })
      .then((r) => setCities(r.cities || [])).catch(() => setCities([]));
  }, [country]);

  // Resolve default country once (account preference → IP geolocation → FR).
  useEffect(() => {
    if (country !== null) return;
    let alive = true;
    (async () => {
      let c = user?.country;
      if (!c) { try { const r = await api("/geo/my-country", { auth: false }); c = r.country; } catch { /* ignore */ } }
      if (alive) setCountry(c || "FR");
    })();
    return () => { alive = false; };
  }, [user, country]);

  const load = useCallback(async () => {
    if (country === null) return;
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (country !== "all") p.set("country", country);
    if (city.trim()) p.set("city", city.trim());
    if (certified) p.set("certified", "true");
    try { setList(await api(`/partners/public/list?${p.toString()}`, { auth: false })); } catch { setList([]); }
  }, [q, country, city, certified]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { setPage(1); }, [q, country, city, certified]);

  const partners = Array.isArray(list) ? list : list?.partners || [];
  const boosted = partners.filter((p) => p.boosted);
  const regular = partners.filter((p) => !p.boosted);
  const { pageItems: regularPage, pages, total } = paginate(regular, page);

  return (
    <div className="bg-background">
      {/* Editorial header banner */}
      <div className="relative h-64 overflow-hidden photo-scrim">
        <img src={IMAGES.heroLobby} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white px-6">
          <span className="inline-flex items-center gap-2 rounded-full badge-red px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide">{t("par.badge")}</span>
          <h1 className="mt-4 font-display text-4xl md:text-5xl font-semibold">{t("par.title")}</h1>
          <p className="mt-2 text-white/85 max-w-xl">{t("par.subtitle")}</p>
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 py-10">
        <div className="mb-8">
          <BoostPromoBanner
            testid="partners-boost-promo"
            title={t("pub.boostEstablishment")}
            subtitle={t("pub.boostEstablishmentDesc")}
            ctaLabel={t("pub.boostFicheCta")}
            to="/b2b/account"
          />
        </div>
        {boosted.length > 0 && (
          <div className="mb-10" data-testid="partners-boosted-top">
            <div className="flex items-center gap-2 mb-4">
              <span className="boost-shine inline-flex items-center gap-1.5 rounded-full bg-findo-red text-white px-3 py-1 text-xs font-bold uppercase tracking-wide" data-testid="boosted-section-badge">
                <Sparkles size={13} /> {t("an.boosted")}
              </span>
              <span className="text-sm text-slate-400">{t("par.badge")}</span>
            </div>
            <BoostedCarousel
              items={boosted}
              keyFor={(p) => p.establishment_id || p.id}
              itemClassName="w-80"
              speed={1.8}
              testid="partners-boosted-carousel"
              renderItem={(p) => <PartnerCard p={p} t={t} />}
            />
          </div>
        )}

        <div className="flex flex-wrap gap-3 items-center justify-center">
          <div className="relative w-full sm:w-80">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("par.searchPh")} className="pl-10 h-11 rounded-full bg-white" data-testid="partners-search" />
          </div>
          <Select value={country || "FR"} onValueChange={setCountry}>
            <SelectTrigger className="h-11 rounded-full w-44 bg-white" data-testid="partners-country"><SelectValue placeholder={t("hc.country")} /></SelectTrigger>
            <SelectContent className="max-h-72 country-scroll">
              <SelectItem value="all">{t("par.allCountries")}</SelectItem>
              {COUNTRIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.flag} {c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={city || "all"} onValueChange={(v) => setCity(v === "all" ? "" : v)}>
            <SelectTrigger className="h-11 rounded-full w-44 bg-white" data-testid="partners-city"><SelectValue placeholder={t("par.cityPh")} /></SelectTrigger>
            <SelectContent className="max-h-72 country-scroll">
              <SelectItem value="all">{t("par.allCities")}</SelectItem>
              {cities.map((c) => <SelectItem key={c.name} value={c.name} data-testid={`partners-city-${c.name}`}>{c.name} ({c.count})</SelectItem>)}
            </SelectContent>
          </Select>
          <button onClick={() => setCertified((v) => !v)} data-testid="partners-certified"
            className={`h-11 px-4 rounded-full text-sm font-semibold flex items-center gap-1.5 transition-colors ${certified ? "bg-findo-navy text-white" : "bg-white border border-border text-slate-600"}`}>
            <BadgeCheck size={16} /> {t("par.certified")}
          </button>
        </div>

        {list === null ? (
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">{Array.from({ length: 10 }).map((_, i) => <div key={i} className="h-64 rounded-[1.5rem] bg-slate-100 animate-pulse" />)}</div>
        ) : partners.length === 0 ? (
          <p className="mt-16 text-center text-slate-400">{t("par.none")}</p>
        ) : regular.length === 0 ? null : (
          <>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4" data-testid="partners-grid">
              {regularPage.map((p, i) => <PartnerCard key={p.establishment_id || p.id} p={p} t={t} index={i} />)}
            </div>
            <Pagination page={page} pages={pages} total={total} onPage={setPage} testid="partners-pagination" />
          </>
        )}
      </div>
    </div>
  );
}
